// FASE 1 — EventsService. Capa única entre UI y datos.
// Mañana se puede sustituir el fetch por otra fuente sin tocar la UI.
import { MOCK_EVENTS, MOCK_VENUES, hydrateMockEvent, hydrateMockVenue } from './mockData';
import { paletteFor } from './designSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ¿Mostrar mocks? Solo con ?demo=1 en la URL o REACT_APP_ENABLE_DEMO=1
export function demoEnabled() {
  try {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1') return true;
  } catch (e) { /* noop */ }
  return process.env.REACT_APP_ENABLE_DEMO === '1';
}

async function safeJson(res) {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function hydrateRealEvent(e) {
  const p = paletteFor(e.venue_id || 0);
  return {
    ...e,
    palette_bg: p.bg,
    palette_icon: p.icon,
    // Si el evento aún no tiene fecha_inicio ISO, intentar derivar ninguna — el UI usará `day` como fallback
    fecha_inicio: e.fecha_inicio || null,
    estilos: e.estilos || [],
    tipo_plan: e.tipo_plan || 'discoteca',
    edad_minima: e.edad_minima ?? 18,
    ultimas_entradas: !!e.ultimas_entradas,
    provider: e.provider || 'stripe',
    puntos: Math.round((e.price || 0) * 10),
  };
}

function hydrateRealVenue(v, allEvents) {
  const p = paletteFor(v.id);
  const upcoming = (allEvents || []).filter((e) => e.venue_id === v.id && !e.inactive).length;
  return {
    ...v,
    palette_bg: p.bg,
    palette_icon: p.icon,
    estilos: v.estilos || [],
    edad_minima: v.edad_minima ?? 18,
    eventos_proximos: upcoming,
  };
}

export const EventsService = {
  async getEvents(opts = {}) {
    const { fecha, filtros = {}, texto = '' } = opts;
    let events = [];
    try {
      const res = await fetch(`${API}/events`, { cache: 'no-store' });
      const real = await safeJson(res);
      events = real.filter((e) => !e.inactive).map(hydrateRealEvent);
    } catch (err) {
      console.warn('getEvents: real fetch failed', err);
    }
    if (demoEnabled()) {
      events = events.concat(MOCK_EVENTS.map(hydrateMockEvent).map(hydrateRealEvent));
    }
    if (fecha) {
      const target = String(fecha).slice(0, 10);
      // Eventos sin fecha_inicio (legacy, p.ej. "day: '2 de Julio'") se muestran siempre.
      events = events.filter((e) => !e.fecha_inicio || e.fecha_inicio.slice(0, 10) === target);
    }
    if (texto) {
      const q = texto.toLowerCase();
      events = events.filter((e) =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.venue || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q)
      );
    }
    if (filtros.estilos && filtros.estilos.length) {
      events = events.filter((e) => (e.estilos || []).some((s) => filtros.estilos.includes(s)));
    }
    if (typeof filtros.precioMax === 'number') {
      events = events.filter((e) => (e.price || 0) <= filtros.precioMax);
    }
    if (filtros.tipoPlan) {
      events = events.filter((e) => e.tipo_plan === filtros.tipoPlan);
    }
    if (filtros.soloMiEdad && filtros.miEdad) {
      events = events.filter((e) => (e.edad_minima || 18) <= filtros.miEdad);
    }
    // Ordenar por fecha_inicio asc, con eventos sin fecha al final
    events.sort((a, b) => {
      const fa = a.fecha_inicio || '9999';
      const fb = b.fecha_inicio || '9999';
      return fa.localeCompare(fb);
    });
    return events;
  },

  async getEvent(id) {
    const list = await this.getEvents();
    return list.find((e) => String(e.id) === String(id)) || null;
  },

  async getVenues() {
    let venues = [];
    let events = [];
    try {
      const [v, e] = await Promise.all([
        fetch(`${API}/venues`, { cache: 'no-store' }).then(safeJson).catch(() => []),
        fetch(`${API}/events`, { cache: 'no-store' }).then(safeJson).catch(() => []),
      ]);
      venues = (v || []).filter((x) => !x.inactive);
      events = e || [];
    } catch (err) { /* noop */ }
    if (demoEnabled()) {
      venues = venues.concat(MOCK_VENUES.map(hydrateMockVenue));
      events = events.concat(MOCK_EVENTS);
    }
    return venues.map((x) => hydrateRealVenue(x, events));
  },

  async getVenue(id) {
    const list = await this.getVenues();
    return list.find((v) => String(v.id) === String(id)) || null;
  },

  async getEventsByVenue(venueId) {
    const list = await this.getEvents();
    return list.filter((e) => String(e.venue_id) === String(venueId));
  },
};

// Días con eventos (para marcar el calendario con puntos)
export async function daysWithEvents() {
  const list = await EventsService.getEvents();
  const set = new Set();
  for (const e of list) {
    if (e.fecha_inicio) set.add(e.fecha_inicio.slice(0, 10));
  }
  return set;
}
